import { OverflowList } from '@theme/overflow-list';
import VariantPicker from '@theme/variant-picker';
import { Component } from '@theme/component';
import { clamp, debounce, isDesktopBreakpoint, mediaQueryLarge, yieldToMainThread } from '@theme/utilities';
import { ThemeEvents, VariantSelectedEvent, VariantUpdateEvent, SlideshowSelectEvent } from '@theme/events';
import { morph } from '@theme/morph';

/**
 * @typedef {object} ProductCardLinkRefs
 * @property {HTMLElement} [cardGallery] - The card gallery element.
 * @property {HTMLImageElement[]} [imagesToTransition] - The images to transition.
 */

/**
 * A custom element for product links with images for transitions to PDP.
 * This is a base class that is extended by ProductCard.
 * Used directly by resource-card.liquid for non-product-card scenarios.
 *
 * @template {ProductCardLinkRefs} [T=ProductCardLinkRefs]
 * @extends {Component<T>}
 */
export class ProductCardLink extends Component {
  get productTransitionEnabled() {
    return this.getAttribute('data-product-transition') === 'true';
  }

  get featuredMediaUrl() {
    return this.getAttribute('data-featured-media-url');
  }

  /**
   * Handles the click event for view transitions.
   * @param {Event} event
   */
  handleViewTransition(event) {
    // If the event has been prevented, don't do anything, another component is handling the click
    if (event.defaultPrevented) return;

    // If the event was on an interactive element, don't do anything, this is not a navigation
    if (event.target instanceof Element) {
      const interactiveElement = event.target.closest('button, input, label, select, [tabindex="1"]');
      if (interactiveElement) return;
    }

    if (!this.productTransitionEnabled) return;

    const { cardGallery } = this.refs;
    if (!cardGallery || !cardGallery.hasAttribute('data-view-transition-to-main-product')) return;

    // Check on the current active image, whether it's a product card image or a resource card image
    const { imagesToTransition } = this.refs;
    const activeImage =
      imagesToTransition?.find(
        (/** @type {HTMLImageElement} */ image) =>
          image.closest('slideshow-slide')?.getAttribute('aria-hidden') === 'false'
      ) || imagesToTransition?.[imagesToTransition.length - 1];

    if (activeImage instanceof HTMLImageElement) this.#setImageSrcset(activeImage);

    cardGallery.setAttribute('data-view-transition-type', 'product-image-transition');
    cardGallery.setAttribute('data-view-transition-triggered', 'true');
  }

  /**
   * Sets the srcset for the image
   * @param {HTMLImageElement} image
   */
  #setImageSrcset(image) {
    if (!this.featuredMediaUrl) return;

    const currentImageUrl = new URL(image.currentSrc);

    // Deliberately not using origin, as it includes the protocol, which is usually skipped for featured media
    const currentImageRawUrl = currentImageUrl.host + currentImageUrl.pathname;

    if (!this.featuredMediaUrl.includes(currentImageRawUrl)) {
      const imageFade = image.animate([{ opacity: 0.8 }, { opacity: 1 }], {
        duration: 125,
        easing: 'ease-in-out',
      });

      imageFade.onfinish = () => {
        image.srcset = this.featuredMediaUrl ?? '';
      };
    }
  }
}

if (!customElements.get('product-card-link')) {
  customElements.define('product-card-link', ProductCardLink);
}

/**
 * A custom element that displays a product card.
 * Extends ProductCardLink to inherit view transition functionality.
 *
 * @typedef {object} ProductCardRefs
 * @property {HTMLAnchorElement} productCardLink - The product card link element.
 * @property {import('slideshow').Slideshow} [slideshow] - The slideshow component.
 * @property {import('quick-add').QuickAddComponent} [quickAdd] - The quick add component.
 * @property {HTMLElement} [cardGallery] - The card gallery component.
 * @property {HTMLElement} [productImageOverlay] - The SKU image overlay container.
 * @property {HTMLImageElement} [productImageOverlayImage] - The SKU image overlay element.
 * @property {HTMLImageElement[]} [imagesToTransition] - The images to transition.
 * @extends {ProductCardLink<ProductCardRefs>}
 */
export class ProductCard extends ProductCardLink {
  requiredRefs = ['productCardLink'];

  get productPageUrl() {
    return this.refs.productCardLink.href;
  }

  /**
   * Gets the currently selected variant ID from the product card
   * @returns {string | null} The variant ID or null if none selected
   */
  getSelectedVariantId() {
    const checkedInput = /** @type {HTMLInputElement | null} */ (
      this.querySelector('input[type="radio"]:checked[data-variant-id]')
    );

    return checkedInput?.dataset.variantId || null;
  }

  /**
   * Gets the product card link element
   * @returns {HTMLAnchorElement | null} The product card link or null
   */
  getProductCardLink() {
    return this.refs.productCardLink || null;
  }

  #fetchProductPageHandler = () => {
    this.refs.quickAdd?.fetchProductPage(this.productPageUrl);
  };

  /**
   * Navigates to a URL link. Respects modifier keys for opening in new tab/window.
   * @param {Event} event - The event that triggered the navigation.
   * @param {URL} url - The URL to navigate to.
   */
  #navigateToURL = (event, url) => {
    // Check for modifier keys that should open in new tab/window (only for mouse events)
    const shouldOpenInNewTab =
      event instanceof MouseEvent && (event.metaKey || event.ctrlKey || event.shiftKey || event.button === 1);

    if (shouldOpenInNewTab) {
      event.preventDefault();
      window.open(url.href, '_blank');
      return;
    } else {
      window.location.href = url.href;
    }
  };

  connectedCallback() {
    super.connectedCallback();

    const link = this.refs.productCardLink;
    if (!(link instanceof HTMLAnchorElement)) throw new Error('Product card link not found');
    this.#handleQuickAdd();

    this.addEventListener(ThemeEvents.variantUpdate, this.#handleVariantUpdate);
    this.addEventListener(ThemeEvents.variantSelected, this.#handleVariantSelected);
    this.addEventListener(SlideshowSelectEvent.eventName, this.#handleSlideshowSelect);
    mediaQueryLarge.addEventListener('change', this.#handleQuickAdd);

    this.addEventListener('click', this.navigateToProduct);
    this.syncVariantOverlay();
    this.#bindCardGalleryHoverEvents();

    // Preload the next image on the slideshow to avoid white flashes on previewImage
    setTimeout(() => {
      if (this.refs.slideshow?.isNested) {
        this.#preloadNextPreviewImage();
      }
    });
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.removeEventListener('click', this.navigateToProduct);
    this.#unbindCardGalleryHoverEvents();
  }

  #bindCardGalleryHoverEvents() {
    const { cardGallery } = this.refs;
    if (!(cardGallery instanceof HTMLElement)) return;

    cardGallery.addEventListener('pointerenter', this.#handleCardGalleryPointerEnter);
    cardGallery.addEventListener('pointermove', this.#handleCardGalleryPointerMove);
    cardGallery.addEventListener('pointerleave', this.#handleCardGalleryPointerLeave);
    cardGallery.addEventListener('mouseenter', this.#handleCardGalleryMouseEnter);
    cardGallery.addEventListener('mousemove', this.#handleCardGalleryMouseMove);
    cardGallery.addEventListener('mouseleave', this.#handleCardGalleryMouseLeave);
  }

  #unbindCardGalleryHoverEvents() {
    const { cardGallery } = this.refs;
    if (!(cardGallery instanceof HTMLElement)) return;

    cardGallery.removeEventListener('pointerenter', this.#handleCardGalleryPointerEnter);
    cardGallery.removeEventListener('pointermove', this.#handleCardGalleryPointerMove);
    cardGallery.removeEventListener('pointerleave', this.#handleCardGalleryPointerLeave);
    cardGallery.removeEventListener('mouseenter', this.#handleCardGalleryMouseEnter);
    cardGallery.removeEventListener('mousemove', this.#handleCardGalleryMouseMove);
    cardGallery.removeEventListener('mouseleave', this.#handleCardGalleryMouseLeave);
  }

  #handleCardGalleryPointerEnter = (event) => {
    if (!(event instanceof PointerEvent)) return;
    this.previewImageAtPointer(event);
  };

  #handleCardGalleryPointerMove = (event) => {
    if (!(event instanceof PointerEvent)) return;
    this.previewImageAtPointer(event);
  };

  #handleCardGalleryPointerLeave = (event) => {
    if (!(event instanceof PointerEvent)) return;
    this.resetImage(event);
  };

  #handleCardGalleryMouseEnter = (event) => {
    if (!(event instanceof MouseEvent)) return;
    this.previewImageAtPointer(event);
  };

  #handleCardGalleryMouseMove = (event) => {
    if (!(event instanceof MouseEvent)) return;
    this.previewImageAtPointer(event);
  };

  #handleCardGalleryMouseLeave = (event) => {
    if (!(event instanceof MouseEvent)) return;
    this.resetImage(event);
  };

  #preloadNextPreviewImage() {
    const currentSlide = this.refs.slideshow?.slides?.[this.refs.slideshow?.current];
    currentSlide?.nextElementSibling?.querySelector('img[loading="lazy"]')?.removeAttribute('loading');
  }

  /**
   * Handles the quick add event.
   */
  #handleQuickAdd = () => {
    this.removeEventListener('pointerenter', this.#fetchProductPageHandler);
    this.removeEventListener('focusin', this.#fetchProductPageHandler);

    if (isDesktopBreakpoint()) {
      this.addEventListener('pointerenter', this.#fetchProductPageHandler);
      this.addEventListener('focusin', this.#fetchProductPageHandler);
    }
  };

  /**
   * Handles the variant selected event.
   * @param {VariantSelectedEvent} event - The variant selected event.
   */
  #handleVariantSelected = (event) => {
    if (event.target !== this.variantPicker) {
      this.variantPicker?.updateSelectedOption(event.detail.resource.id);
    }
  };

  /**
   * Handles the variant update event.
   * Updates price, checks for unavailable variants, and updates product URL.
   * @param {VariantUpdateEvent} event - The variant update event.
   */
  #handleVariantUpdate = (event) => {
    // Stop the event from bubbling up to the section, variant updates triggered from product cards are fully handled
    // by this component and should not affect anything outside the card.
    event.stopPropagation();

    this.updatePrice(event);
    this.#isUnavailableVariantSelected(event);
    this.#updateProductUrl(event);
    this.refs.quickAdd?.fetchProductPage(this.productPageUrl);

    if (event.target !== this.variantPicker) {
      this.variantPicker?.updateVariantPicker(event.detail.data.html);
    }

    this.#updateVariantImages();
    this.syncVariantOverlay();
    this.#previousSlideIndex = null;

    // Remove attribute after re-rendering since a variant selection has been made
    this.removeAttribute('data-no-swatch-selected');

    // Force overflow list to reflow after variant update
    // This fixes an issue where the overflow counter doesn't update properly in some browsers
    this.#updateOverflowList();
  };

  /**
   * Forces the overflow list to recalculate by dispatching a reflow event.
   * This ensures the overflow counter displays correctly after variant updates.
   */
  #updateOverflowList() {
    // Find the overflow list in the variant picker
    const overflowList = this.querySelector('swatches-variant-picker-component overflow-list');
    const isActiveOverflowList = overflowList?.querySelector('[slot="overflow"]') ? true : false;
    if (!overflowList || !isActiveOverflowList) return;

    // Use requestAnimationFrame to ensure DOM has been updated
    requestAnimationFrame(() => {
      // Dispatch a reflow event to trigger recalculation
      overflowList.dispatchEvent(
        new CustomEvent('reflow', {
          bubbles: true,
          detail: {},
        })
      );
    });
  }

  /**
   * Updates the DOM with a new price.
   * @param {VariantUpdateEvent} event - The variant update event.
   */
  updatePrice(event) {
    const priceContainer = this.querySelectorAll(`product-price [ref='priceContainer']`)[1];
    const newPriceElement = event.detail.data.html.querySelector(`product-price [ref='priceContainer']`);

    if (newPriceElement && priceContainer) {
      morph(priceContainer, newPriceElement);
    }
  }

  /**
   * Updates the product URL based on the variant update event.
   * @param {VariantUpdateEvent} event - The variant update event.
   */
  #updateProductUrl(event) {
    const responseProductCard = event.detail.data.html?.querySelector('product-card');
    const anchorElement = responseProductCard?.querySelector('a');
    const featuredMediaUrl = responseProductCard?.getAttribute('data-featured-media-url');

    // Update the featured media URL for view transitions (inherited from ProductCardLink)
    if (featuredMediaUrl) {
      this.setAttribute('data-featured-media-url', featuredMediaUrl);
    }

    if (anchorElement instanceof HTMLAnchorElement) {
      // If the href is empty, don't update the product URL eg: unavailable variant
      if (anchorElement.getAttribute('href')?.trim() === '') return;

      const productUrl = anchorElement.href;
      const { productCardLink, productTitleLink, cardGalleryLink } = this.refs;

      productCardLink.href = productUrl;
      if (cardGalleryLink instanceof HTMLAnchorElement) {
        cardGalleryLink.href = productUrl;
      }
      if (productTitleLink instanceof HTMLAnchorElement) {
        productTitleLink.href = productUrl;
      }
    }
  }

  /**
   * Checks if an unavailable variant is selected.
   * @param {VariantUpdateEvent} event - The variant update event.
   */
  #isUnavailableVariantSelected(event) {
    const allVariants = /** @type {NodeListOf<HTMLInputElement>} */ (
      event.detail.data.html.querySelectorAll('input:checked')
    );

    for (const variant of allVariants) {
      this.#toggleAddToCartButton(variant.dataset.optionAvailable === 'true');
    }
  }

  /**
   * Toggles the add to cart button state.
   * @param {boolean} enable - Whether to enable or disable the button.
   */
  #toggleAddToCartButton(enable) {
    const addToCartButton = this.querySelector('.add-to-cart__button button');

    if (addToCartButton instanceof HTMLButtonElement) {
      addToCartButton.disabled = !enable;
    }
  }

  /**
   * Updates the SKU image overlay visibility and content.
   * @param {string | null | undefined} imageUrl
   * @param {string} [imageAlt]
   */
  #setVariantOverlay(imageUrl, imageAlt = '') {
    const { productImageOverlay, productImageOverlayImage } = this.refs;

    if (!(productImageOverlay instanceof HTMLElement) || !(productImageOverlayImage instanceof HTMLImageElement)) {
      return;
    }

    if (!imageUrl) {
      productImageOverlay.hidden = true;
      productImageOverlay.removeAttribute('data-visible');
      productImageOverlayImage.removeAttribute('src');
      productImageOverlayImage.alt = '';
      return;
    }

    productImageOverlayImage.src = imageUrl;
    productImageOverlayImage.alt = imageAlt;
    productImageOverlay.hidden = false;
    productImageOverlay.setAttribute('data-visible', 'true');
  }

  /**
   * Returns the selected swatch overlay image data, if any.
   * @returns {{ url: string | null, alt: string }}
   */
  #getSelectedSwatchOverlayData() {
    const selectedOption = this.variantPicker?.selectedOption;

    if (!(selectedOption instanceof HTMLInputElement)) {
      return { url: null, alt: '' };
    }

    return {
      url: selectedOption.dataset.optionImageUrl || null,
      alt: selectedOption.dataset.optionImageAlt || selectedOption.getAttribute('aria-label') || '',
    };
  }

  /**
   * Shows the SKU image overlay for the swatch mapped to a media id.
   * @param {string | number} id
   */
  showVariantOverlayByMediaId(id) {
    const swatchInput = this.querySelector(`input[data-option-media-id="${CSS.escape(String(id))}"]`);

    if (!(swatchInput instanceof HTMLInputElement)) {
      return;
    }

    this.#setVariantOverlay(
      swatchInput.dataset.optionImageUrl || null,
      swatchInput.dataset.optionImageAlt || swatchInput.getAttribute('aria-label') || ''
    );
  }

  /**
   * Syncs the SKU image overlay with the currently selected swatch.
   */
  syncVariantOverlay() {
    const { url, alt } = this.#getSelectedSwatchOverlayData();
    this.#setVariantOverlay(url, alt);
  }

  /**
   * Clears the current swatch selection and hides the SKU overlay.
   */
  #clearSelectedSwatch() {
    const variantPicker = this.variantPicker;

    if (!(variantPicker instanceof HTMLElement)) {
      this.#setVariantOverlay(null);
      return;
    }

    const swatchInputs = /** @type {NodeListOf<HTMLInputElement>} */ (
      variantPicker.querySelectorAll('input[type="radio"][name$="-swatch"]')
    );

    let clearedSelection = false;

    for (const input of swatchInputs) {
      if (input.checked) {
        clearedSelection = true;
      }

      input.checked = false;
      input.dataset.currentChecked = 'false';
      input.dataset.previousChecked = 'false';
    }

    this.#setVariantOverlay(null);

    if (clearedSelection) {
      this.setAttribute('data-no-swatch-selected', '');
    }
  }

  /**
   * Hide the variant images that are not for the selected variant.
   */
  #updateVariantImages() {
    const { slideshow } = this.refs;
    if (!this.variantPicker?.selectedOption) {
      return;
    }

    const selectedImageId = this.variantPicker?.selectedOption.dataset.optionMediaId;

    if (slideshow && selectedImageId) {
      const { slides = [] } = slideshow.refs;

      for (const slide of slides) {
        if (slide.getAttribute('variant-image') == null) continue;

        slide.hidden = slide.getAttribute('slide-id') !== selectedImageId;
      }

      slideshow.select({ id: selectedImageId }, undefined, { animate: false });
    }
  }

  /**
   * Gets all variant inputs.
   * @returns {NodeListOf<HTMLInputElement>} All variant input elements.
   */
  get allVariants() {
    return this.querySelectorAll('input[data-variant-id]');
  }

  /**
   * Gets the variant picker component.
   * @returns {VariantPicker | null} The variant picker component.
   */
  get variantPicker() {
    return this.querySelector('swatches-variant-picker-component');
  }
  /** @type {number | null} */
  #previousSlideIndex = null;
  /** @type {number | null} */
  #hoverPreviewSlideIndex = null;

  /**
   * @returns {HTMLElement[]}
   */
  #hoverProgressSteps() {
    const { cardGallery } = this.refs;
    if (!(cardGallery instanceof HTMLElement)) return [];

    return Array.from(cardGallery.querySelectorAll('[data-hover-progress-step]'));
  }

  /**
   * @param {number} zoneIndex
   */
  #setActiveHoverProgressStep(zoneIndex) {
    const steps = this.#hoverProgressSteps();
    if (!steps.length) return;

    for (const [index, step] of steps.entries()) {
      if (index === zoneIndex) {
        step.setAttribute('data-active', 'true');
      } else {
        step.removeAttribute('data-active');
      }
    }
  }

  #resetHoverProgressSteps() {
    const steps = this.#hoverProgressSteps();
    if (!steps.length) return;

    for (const step of steps) {
      step.removeAttribute('data-active');
    }
  }

  /**
   * Handles the slideshow select event.
   * @param {SlideshowSelectEvent} event - The slideshow select event.
   */
  #handleSlideshowSelect = (event) => {
    if (event.detail.userInitiated) {
      this.#previousSlideIndex = event.detail.index;
      this.#clearSelectedSwatch();
    }
  };

  /**
   * Previews a variant.
   * @param {string} id - The id of the variant to preview.
   */
  previewVariant(id) {
    const { slideshow } = this.refs;

    if (!slideshow) return;

    this.resetVariant.cancel();
    slideshow.select({ id }, undefined, { animate: false });
  }

  /**
   * Previews the next image.
   * @param {PointerEvent} event - The pointer event.
   */
  previewImage(event) {
    if (event.pointerType !== 'mouse') return;

    const { slideshow } = this.refs;

    if (!slideshow) return;

    this.resetVariant.cancel();

    if (this.#previousSlideIndex != null && this.#previousSlideIndex > 0) {
      slideshow.select(this.#previousSlideIndex, undefined, { animate: false });
    } else {
      slideshow.next(undefined, { animate: false });
      setTimeout(() => this.#preloadNextPreviewImage());
    }
  }

  /**
   * Previews a product image based on pointer position zones.
   * @param {PointerEvent | MouseEvent} event - The pointer or mouse event.
   */
  previewImageAtPointer(event) {
    if (event instanceof PointerEvent && event.pointerType !== 'mouse') return;
    if (!(event instanceof PointerEvent) && !(event instanceof MouseEvent)) return;

    const { slideshow, cardGallery } = this.refs;

    if (!slideshow || !(cardGallery instanceof HTMLElement)) return;

    const slides = slideshow.slides;
    if (!slides || slides.length < 2) return;

    this.resetVariant.cancel();

    const productImageCount = Math.max(slides.length - 1, 0);
    cardGallery.setAttribute('data-product-image-count', String(productImageCount));

    if (productImageCount === 0) return;

    const rect = cardGallery.getBoundingClientRect();
    if (!rect.width) return;

    const relativeX = clamp(event.clientX - rect.left, 0, rect.width);
    const zoneIndex = Math.min(
      productImageCount - 1,
      Math.max(0, Math.floor((relativeX / rect.width) * productImageCount))
    );
    const targetSlideIndex = zoneIndex + 1;

    if (this.#hoverPreviewSlideIndex === targetSlideIndex) return;

    this.#hoverPreviewSlideIndex = targetSlideIndex;
    this.#setActiveHoverProgressStep(zoneIndex);
    slideshow.select(targetSlideIndex);
  }

  /**
   * Previews a product image by hover zone index.
   * @param {string | number} zoneIndex
   * @param {PointerEvent | MouseEvent} [event]
   */
  previewImageByZone(zoneIndex, event) {
    if (event instanceof PointerEvent && event.pointerType !== 'mouse') return;
    if (event && !(event instanceof PointerEvent) && !(event instanceof MouseEvent)) return;

    const { slideshow } = this.refs;
    if (!slideshow) return;

    const slides = slideshow.slides;
    if (!slides || slides.length < 2) return;

    const parsedZoneIndex = Number.parseInt(String(zoneIndex), 10);
    if (Number.isNaN(parsedZoneIndex)) return;

    const productImageCount = Math.max(slides.length - 1, 0);
    if (productImageCount === 0) return;
    if (parsedZoneIndex < 0 || parsedZoneIndex >= productImageCount) return;

    const targetSlideIndex = parsedZoneIndex + 1;
    if (this.#hoverPreviewSlideIndex === targetSlideIndex) return;

    this.resetVariant.cancel();
    this.#hoverPreviewSlideIndex = targetSlideIndex;
    this.#setActiveHoverProgressStep(parsedZoneIndex);
    slideshow.select(targetSlideIndex);
  }

  /**
   * Resets the image to the main product image.
   * @param {PointerEvent | MouseEvent} event - The pointer or mouse event.
   */
  resetImage(event) {
    if (event instanceof PointerEvent && event.pointerType !== 'mouse') return;
    if (!(event instanceof PointerEvent) && !(event instanceof MouseEvent)) return;

    this.#hoverPreviewSlideIndex = null;
    this.#resetHoverProgressSteps();
    this.#selectMainSlide();
  }

  /**
   * Selects the first visible slide (main image).
   */
  #selectMainSlide() {
    const { slideshow } = this.refs;
    if (!slideshow || !slideshow.slides?.length) return;

    slideshow.select(0);
  }

  /**
   * Resets the image to the variant image.
   */
  #resetVariant = () => {
    const { slideshow } = this.refs;

    if (!slideshow) return;

    if (this.variantPicker?.selectedOption) {
      const id = this.variantPicker.selectedOption.dataset.optionMediaId;
      if (id) {
        slideshow.select({ id }, undefined, { animate: false });
        return;
      }
    }

    const initialSlide = slideshow.initialSlide;
    const slideId = initialSlide?.getAttribute('slide-id');
    if (initialSlide && slideshow.slides?.includes(initialSlide) && slideId) {
      slideshow.select({ id: slideId }, undefined, { animate: false });
      return;
    }

    slideshow.previous(undefined, { animate: false });
  };

  /**
   * Intercepts the click event on the product card anchor, we want
   * to use this to add an intermediate state to the history.
   * This intermediate state captures the page we were on so that we
   * navigate back to the same page when the user navigates back.
   * In addition to that, it captures the product card anchor so that we
   * have the specific product card in view.
   *
   * A product card can have other interactive elements like variant picker,
   * so we do not navigate if the click was on one of those elements.
   *
   * @param {Event} event
   */
  navigateToProduct = (event) => {
    if (!(event.target instanceof Element)) return;

    // Don't navigate if this product card is marked as no-navigation (e.g., in theme editor)
    if (this.hasAttribute('data-no-navigation')) return;

    const interactiveElement = event.target.closest('button, input, label, select, [tabindex="1"]');

    // If the click was on an interactive element, do nothing.
    if (interactiveElement) {
      return;
    }

    const link = this.refs.productCardLink;
    if (!link.href) return;
    const linkURL = new URL(link.href);

    const productCardAnchor = link.getAttribute('id');
    if (!productCardAnchor) return;

    const infiniteResultsList = this.closest('results-list[infinite-scroll="true"]');
    if (!window.Shopify.designMode && infiniteResultsList) {
      const url = new URL(window.location.href);
      const parent = this.closest('li');
      url.hash = productCardAnchor;
      if (parent && parent.dataset.page) {
        url.searchParams.set('page', parent.dataset.page);
      }

      yieldToMainThread().then(() => {
        history.replaceState({}, '', url.toString());
      });
    }

    const targetLink = event.target.closest('a');
    // Let the native navigation handle the click if it was on a link.
    if (!targetLink) {
      this.#navigateToURL(event, linkURL);
    }
  };

  /**
   * Resets the variant.
   */
  resetVariant = debounce(this.#resetVariant, 100);
}

if (!customElements.get('product-card')) {
  customElements.define('product-card', ProductCard);
}

/**
 * A custom element that displays a variant picker with swatches.
 * @typedef {import('@theme/variant-picker').VariantPickerRefs & {overflowList: HTMLElement}} SwatchesRefs
 */

/**
 * @extends {VariantPicker<SwatchesRefs>}
 */
class SwatchesVariantPickerComponent extends VariantPicker {
  connectedCallback() {
    super.connectedCallback();

    // Cache the parent product card
    this.parentProductCard = this.closest('product-card');

    // Listen for variant updates to apply pending URL changes
    this.addEventListener(ThemeEvents.variantUpdate, this.#handleCardVariantUrlUpdate.bind(this));
  }

  /**
   * Updates the card URL when a variant is selected.
   */
  #handleCardVariantUrlUpdate() {
    if (this.pendingVariantId && this.parentProductCard instanceof ProductCard) {
      const currentUrl = new URL(this.parentProductCard.refs.productCardLink.href);
      currentUrl.searchParams.set('variant', this.pendingVariantId);
      this.parentProductCard.refs.productCardLink.href = currentUrl.toString();
      this.pendingVariantId = null;
    }
  }

  /**
   * Override the variantChanged method to handle unavailable swatches with available alternatives.
   * @param {Event} event - The variant change event.
   */
  variantChanged(event) {
    if (!(event.target instanceof HTMLElement)) return;

    // Check if this is a swatch input
    const isSwatchInput = event.target instanceof HTMLInputElement && event.target.name?.includes('-swatch');
    const clickedSwatch = event.target;
    const availableCount = parseInt(clickedSwatch.dataset.availableCount || '0');
    const firstAvailableVariantId = clickedSwatch.dataset.firstAvailableOrFirstVariantId;

    // For swatch inputs, check if we need special handling
    if (isSwatchInput && availableCount > 0 && firstAvailableVariantId) {
      // If this is an unavailable variant but there are available alternatives
      // Prevent the default handling
      event.stopPropagation();

      // Update the selected option visually
      this.updateSelectedOption(clickedSwatch);

      // Build request URL with the first available variant
      const productUrl = this.dataset.productUrl?.split('?')[0];

      if (!productUrl) return;

      const url = new URL(productUrl, window.location.origin);
      url.searchParams.set('variant', firstAvailableVariantId);
      url.searchParams.set('section_id', 'section-rendering-product-card');

      const requestUrl = url.href;

      // Store the variant ID we want to apply to the URL
      this.pendingVariantId = firstAvailableVariantId;

      // Use parent's fetch method
      this.fetchUpdatedSection(requestUrl);
      return;
    }

    // For all other cases, use the default behavior
    super.variantChanged(event);
  }

  /**
   * Shows all swatches.
   * @param {Event} [event] - The event that triggered the show all swatches.
   */
  showAllSwatches(event) {
    event?.preventDefault();

    const { overflowList } = this.refs;

    if (overflowList instanceof OverflowList) {
      overflowList.showAll();
    }
  }
}

if (!customElements.get('swatches-variant-picker-component')) {
  customElements.define('swatches-variant-picker-component', SwatchesVariantPickerComponent);
}
